import { defineStore } from 'pinia';
import {
  formattingTime,
  validateForPhoneNumber,
  validateForEmail,
  validateForCertificationCode,
} from '@/utils/preorderUtil';
import { merge, extend, cloneDeep, assignIn, isEqual } from 'lodash';
// import { ErrorMessageObj } from 'src/components/modules/bannerCoupon/BannerCoupon';
import { useBannerCouponStore as bannerCouponStore } from '@/store/bannerCoupon';
import { usePreorderStore as preorderStore } from '@/store/preorder';
import { i18n, tHtml } from '@/utils/i18n';
import { IParentData } from '@/views/client/Container.vue';
import { device } from '@/components/modules/common/module';
const { t } = i18n;

type ErrorMessageKey = {
  preorder: PreorderErrorMessageObj;
  bannerCoupon: BannerCouponErrorMessageObj;
};

type PreorderErrorMessageObj = {
  select_agree: string;
  birth: string;
  inputPhone: string;
  inputEmail: string;
  inputCertify: string;
  inputOptionsEmail: string;
  disableCertificationButton: boolean;
  text: string;
};
type BannerCouponErrorMessageObj = {
  inputPhone: string; // 휴대 전화 오류
  inputEmail: string; // 이메일 오류
  inputCertify: string; // 인증번호 오류
  common: string; // 공통 오류
  disableCertificationButton: boolean;
};
interface IPreorderCommon {
  timer: number;
  totalTime: number;
  timerStr: string;
  debounce: number | undefined;
  parentData: IParentData;
  // 쿠폰조회, 사전예약의 에러 상태 관리
  errorMessage: ErrorMessageKey;
}
// 사전예약, 쿠폰조회에 공통으로 사용 되는 store
// Input error, Timer, parentData

export const usePreorderCommonStore = defineStore('preorderCommon', {
  state: (): IPreorderCommon => ({
    timer: 0,
    totalTime: 0,
    timerStr: '',
    debounce: undefined,
    parentData: {
      colors: {
        a: '',
        b: '',
        font: '',
      },
      locale: 'ko',
      preorderStartDate: '',
      preorderEndDate: '',
      preorderSubType: 'PHONE',
      gameName: '',
      usingAreaCode: false,
      specificCountryNumber: '',
      usingDropdown: false,
      useCertification: false,
      useSelectStore: false,
      dropdownOptions: [],
      phonePattern: /^((010|011|016|017|018|019)[0-9]{7,8})$/g,
      emailPattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/g,
      loginUserInfo: { phoneNumber: '', email: '' },
      loginUserPreorderHistories: [],
      settings: {
        coupon: {
          type: 'IMMEDIATE',
          selectableCouponCount: 0,
          displayStartDate: '',
          displayEndDate: '',
          registerStartDate: '',
          registerEndDate: '',
          selectable: false,
          coupons: [],
        },
      },
    },
    // 쿠폰조회, 사전예약의 에러 상태 관리
    errorMessage: {
      preorder: {
        select_agree: '',
        birth: '',
        inputPhone: '',
        inputOptionsEmail: '',
        inputEmail: '',
        inputCertify: '',
        disableCertificationButton: false,
        text: '',
      },
      bannerCoupon: {
        inputPhone: '', // 휴대 전화 오류
        inputEmail: '', // 이메일 오류
        inputCertify: '', // 인증번호 오류
        common: '', // 공통 오류
        disableCertificationButton: false,
      },
    },
  }),
  getters: {
    // PHONE or EMAIL 타입인지
    getSubType(state: IPreorderCommon) {
      return state.parentData.preorderSubType;
    },
    getUsePhone(state: IPreorderCommon) {
      return state.parentData.preorderSubType === 'PHONE';
    },
    getUseEmail(state: IPreorderCommon) {
      return state.parentData.preorderSubType === 'EMAIL';
    },
    // 인증을 사용하는지
    getUseCertification(state: IPreorderCommon) {
      return state.parentData.useCertification;
    },
    // specificCountryNumber가 있는 경우 지역번호로 사용됨
    // 010, 016, 019.... 등
    // 운영툴에서 설정에 잡아주는거라 다른 방법으로 막아주는 처리 필요할 듯(?)

    // 드롭다운을 사용하고, 지역코드도 있고, dropdownoption도 있으면 지역번호사용

    getUsingAreaCode(state: IPreorderCommon) {
      return state.parentData.usingAreaCode;
      // return (
      //   state.parentData.usingDropdown &&
      //   state.parentData.specificCountryNumber.length !== 0 &&
      //   state.parentData.dropdownOptions.length !== 0
      // );
    },
    // 마켓 선택을 사용하는지
    getUseSelectStore(state: IPreorderCommon) {
      return state.parentData.useSelectStore;
    },
    getUsingDropDown(state: IPreorderCommon) {
      return state.parentData.usingDropdown;
    },
    getDropDownOptions(state: IPreorderCommon) {
      return state.parentData.dropdownOptions;
    },
    getGameName(state: IPreorderCommon) {
      return state.parentData.gameName;
    },
    getUseSelectReward(state: IPreorderCommon) {
      return state.parentData.settings.coupon.selectable;
    },
    getSelectableCouponCount(state: IPreorderCommon) {
      return state.parentData.settings.coupon.selectableCouponCount;
    },
    getCouponType(state: IPreorderCommon): 'IMMEDIATE' | 'NONE' | 'LATER' | '' {
      return state.parentData.settings.coupon.type;
    },
    getCouponDisplayStartDate(state: IPreorderCommon) {
      return state.parentData.settings.coupon.displayStartDate;
    },
    getCouponDisplayEndDate(state: IPreorderCommon) {
      return state.parentData.settings.coupon.displayEndDate;
    },
    getCouponRegisterStartDate(state: IPreorderCommon) {
      return state.parentData.settings.coupon.registerStartDate;
    },
    getCouponRegisterEndDate(state: IPreorderCommon) {
      return state.parentData.settings.coupon.registerEndDate;
    },
    getSpecificCountryNumber(state: IPreorderCommon) {
      return state.parentData.specificCountryNumber;
    },
    getCouponRegistTime(state: IPreorderCommon) {
      return [
        state.parentData.settings.coupon.registerStartDate,
        state.parentData.settings.coupon.registerStartDate,
      ];
    },
    getTimerTime(state: IPreorderCommon) {
      return state.parentData.preorderSubType === 'PHONE' ? 300 : 600;
    },
    getLoginUserInfo(state: IPreorderCommon) {
      return state.parentData.loginUserInfo;
    },
    getTimer(state: IPreorderCommon) {
      return state.timer;
    },

    // 에러가 있으면 true
    getHasPreorderError(state: IPreorderCommon) {
      return !(
        !state.errorMessage.preorder.birth &&
        !state.errorMessage.preorder.inputPhone &&
        !state.errorMessage.preorder.inputOptionsEmail &&
        !state.errorMessage.preorder.inputEmail &&
        !state.errorMessage.preorder.text
      );
    },
    getLocale(state: IPreorderCommon) {
      return state.parentData.locale;
    },

    getIsArabic(state: IPreorderCommon) {
      return state.parentData.locale.split('_')[0] === 'ar';
    },
  },
  actions: {
    setParentData(parentData: any) {
      this.parentData = assignIn(this.parentData, parentData);
      // parentData가 set한 이후에 전화번호, 이메일 데이터 input에 삽입
      preorderStore().setLoginUserPhoneOrEmail();
    },

    // 전화번호 유효성 검사
    // 오류 있으면 true
    // 오류 없으면 false
    validatePhoneNumber(errorType: keyof ErrorMessageKey) {
      const phoneInput =
        errorType === 'preorder'
          ? preorderStore().getInputedPhone
          : bannerCouponStore().getInputedPhone;

      const focusTarget =
        errorType === 'preorder'
          ? preorderStore().phoneOrEmailInput
          : bannerCouponStore().bannerPhoneOrEmailInput;

      this.errorMessage[errorType].inputPhone = validateForPhoneNumber(
        phoneInput,
        this.parentData.phonePattern,
        focusTarget,
        device.value === 'pc'
      );
      return this.errorMessage[errorType].inputPhone.length === 0;
    },

    // 이메일 유효성 검사
    // 오류 있으면 true
    // 오류 없으면 false
    validateEmail(errorType: keyof ErrorMessageKey) {
      const emailInput =
        errorType === 'preorder'
          ? preorderStore().step2Data.emailStr
          : bannerCouponStore().userInputedData.inputedEmail;

      const focusTarget =
        errorType === 'preorder'
          ? preorderStore().phoneOrEmailInput
          : bannerCouponStore().bannerPhoneOrEmailInput;

      this.errorMessage[errorType].inputEmail = validateForEmail(
        emailInput,
        this.parentData.emailPattern,
        focusTarget,
        true
      );

      return this.errorMessage[errorType].inputEmail.length === 0;
    },

    // (선택) 이메일 유효성 검사
    validateOptionsEmail() {
      // 선택 이메일 사용할 경우
      if (preorderStore().step2Data.optionalEmailStr.length === 0) return true;
      this.errorMessage.preorder.inputOptionsEmail = validateForEmail(
        preorderStore().step2Data.optionalEmailStr,
        this.parentData.emailPattern,
        preorderStore().optionsEmailInput,
        device.value === 'pc'
      );
      return this.errorMessage.preorder.inputOptionsEmail.length === 0;
    },

    // 인증번호 유효성 검사
    // 오류 있으면 true
    // 오류 없으면 false
    validateCertificationCode(errorType: keyof ErrorMessageKey) {
      const certifyInput =
        errorType === 'preorder'
          ? preorderStore().step2Data.certificationCode
          : bannerCouponStore().userInputedData.inputedCertifyNum;

      const focusTarget =
        errorType === 'preorder'
          ? preorderStore().certificationInput
          : bannerCouponStore().bannerCertifyInput;

      this.errorMessage[errorType].inputCertify = validateForCertificationCode(
        certifyInput,
        focusTarget,
        device.value === 'pc'
      );

      return this.errorMessage[errorType].inputCertify.length === 0;
    },

    // 전화번호, 이메일, 인증번호 모두 검사
    validateEmailOrPhoneAndCertification(errorType: keyof ErrorMessageKey) {
      let phone = true;
      let email = true;
      let certify = true;
      if (this.getUsePhone) {
        // 추가 이메일 주소 있을시 추가 이메일 주소까지 검사
        if (errorType === 'preorder' && preorderStore().step2Data.useOptionalEmailField) {
          phone = this.validatePhoneNumber(errorType) && this.validateOptionsEmail();
        } else phone = this.validatePhoneNumber(errorType);
      }
      if (this.getUseEmail) email = this.validateEmail(errorType);
      if (errorType === 'bannerCoupon' || this.getUseCertification)
        certify = this.validateCertificationCode(errorType);

      return phone && email && certify;
    },

    // 애러 메시지 리셋
    resetErrorMessage() {
      const DefaultErrorMessage = {
        preorder: {
          select_agree: '',
          birth: '',
          inputPhone: '',
          inputOptionsEmail: '',
          inputEmail: '',
          inputCertify: '',
          disableCertificationButton: false,
          text: '',
        },
        bannerCoupon: {
          inputPhone: '', // 휴대 전화 오류
          inputEmail: '', // 이메일 오류
          inputCertify: '', // 인증번호 오류
          common: '', // 공통 오류
          hasError: false,
          disableCertificationButton: false,
        },
      };
      // 한번만 리셋하도록 적용
      if (isEqual(this.errorMessage, DefaultErrorMessage)) return;
      this.errorMessage = merge({}, DefaultErrorMessage);
    },

    // ----- 타이머 관련 함수 -----
    initTimer(setTime: number, errorType: keyof ErrorMessageKey): number {
      this.totalTime = setTime;
      this.timerStr = formattingTime(this.totalTime);
      let interval = window.setInterval(() => {
        this.totalTime--;
        this.timerStr = formattingTime(this.totalTime);
        if (this.totalTime <= 0) {
          this.stopTimer(interval);
          // 0초 되면 에러 후 focus
          this._setTimerZeroError(errorType);
        }
      }, 1000);
      return interval;
    },
    // 0초 되면 에러
    _setTimerZeroError(errorType: keyof ErrorMessageKey) {
      // 0초 되면 에러 후 focus
      if (errorType === 'preorder') {
        this.errorMessage.preorder.inputCertify = t('preorder.error.text.certification_time_limit');
        preorderStore().certificationInput?.focus();
      } else {
        this.errorMessage.bannerCoupon.inputCertify = t(
          'preorder.error.text.certification_time_limit'
        );
        bannerCouponStore().bannerCertifyInput?.focus();
      }
    },
    stopTimer(timer: number): void {
      if (timer !== null) clearInterval(timer);
    },
    startTimer(errorType: keyof ErrorMessageKey): void {
      const setTime = this.getTimerTime;
      // 기존에 타이머가 있으면 제거
      this.stopTimer(this.timer);
      this.timer = 0;
      this.timer = this.initTimer(setTime, errorType);
    },
    restartTimer(setTime: number, errorType: keyof ErrorMessageKey): void {
      this.stopTimer(this.timer);
      this.timer = this.initTimer(setTime, errorType);
    },
  },
});
